import type { TargetDataPoint } from '@/api/targetDataPoints';
import type { ClientProgramTarget } from '@/api/clientProgramTargets';
import type { Aggregation, DateRange, GraphConfig } from '@/api/targetGraphConfigs';

// Transforms raw data points + a graph config into Recharts-ready series.
// Kept as a pure function so it can be unit-tested independently of React.

interface ChartPoint {
    // ISO date (YYYY-MM-DD) — used as x-axis categorical key.
    date: string;
    // Display label on the axis (short form for weekly buckets).
    label: string;
    // Numeric value at this bucket (mean/sum/last depending on aggregation).
    value: number;
    // Optional y-value on the fitted trendline at this bucket.
    trend?: number;
    // Number of raw points that fed this bucket (for tooltip).
    count: number;
}

interface GraphDataResult {
    points: ChartPoint[];
    criterionValue: number | null;
    unitSuffix: string;
    yDomain: [number | 'auto', number | 'auto'];
}

const MS_PER_DAY = 86_400_000;

function startOfWeekISO(dateStr: string): string {
    const d = new Date(`${dateStr}T00:00:00`);
    // Monday-start week (common clinical convention; Sunday-based ABA tools
    // usually plot weekly means and nobody complains either way).
    const day = d.getDay();
    const diff = (day + 6) % 7;
    d.setDate(d.getDate() - diff);
    return d.toISOString().slice(0, 10);
}

function formatLabel(dateStr: string, agg: Aggregation): string {
    const d = new Date(`${dateStr}T00:00:00`);
    if (agg === 'weekly_avg') {
        return `${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
    }
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function applyDateRange(points: TargetDataPoint[], range: DateRange, customStart: string | null, customEnd: string | null): TargetDataPoint[] {
    if (range === 'all') return points;
    let startMs: number | null = null;
    let endMs: number | null = null;
    if (range === 'custom') {
        if (!customStart || !customEnd) return points;
        startMs = new Date(`${customStart}T00:00:00`).getTime();
        endMs   = new Date(`${customEnd}T23:59:59.999`).getTime();
    } else {
        const days = range === '30d' ? 30 : range === '60d' ? 60 : 90;
        endMs   = Date.now();
        startMs = endMs - days * MS_PER_DAY;
    }
    return points.filter((p) => {
        const t = new Date(p.collectedAt).getTime();
        return t >= (startMs as number) && t <= (endMs as number);
    });
}

// Compute linear regression over (x=days-since-first, y=value). Returns a
// function mapping a ChartPoint.date back to the fitted y value, or null if
// fewer than 2 buckets are available.
function fitTrend(points: ChartPoint[]): ((date: string) => number) | null {
    if (points.length < 2) return null;
    const epoch = new Date(`${points[0].date}T00:00:00`).getTime();
    const xs = points.map((p) => (new Date(`${p.date}T00:00:00`).getTime() - epoch) / MS_PER_DAY);
    const ys = points.map((p) => p.value);
    const n = xs.length;
    const sumX = xs.reduce((a, b) => a + b, 0);
    const sumY = ys.reduce((a, b) => a + b, 0);
    const sumXY = xs.reduce((a, _b, i) => a + xs[i] * ys[i], 0);
    const sumXX = xs.reduce((a, b) => a + b * b, 0);
    const denom = n * sumXX - sumX * sumX;
    if (denom === 0) return null;
    const slope = (n * sumXY - sumX * sumY) / denom;
    const intercept = (sumY - slope * sumX) / n;
    return (date: string) => {
        const x = (new Date(`${date}T00:00:00`).getTime() - epoch) / MS_PER_DAY;
        return slope * x + intercept;
    };
}

function unitSuffixFor(dataType: ClientProgramTarget['dataType']): string {
    if (dataType === 'percent' || dataType === 'trial' || dataType === 'task_analysis') return '%';
    if (dataType === 'duration') return 's';
    return '';
}

export function buildGraphData(
    rawPoints: TargetDataPoint[],
    config: GraphConfig,
    target: Pick<ClientProgramTarget, 'dataType' | 'masteryValue'>,
): GraphDataResult {
    const filtered = applyDateRange(rawPoints, config.dateRange, config.customStart, config.customEnd);

    // Bucket by date (or week) depending on aggregation.
    type Bucket = { date: string; sum: number; count: number; last: number };
    const buckets = new Map<string, Bucket>();
    for (const p of filtered) {
        const dayStr = new Date(p.collectedAt).toISOString().slice(0, 10);
        const key = config.aggregation === 'weekly_avg' ? startOfWeekISO(dayStr) : dayStr;
        const v = Number(p.valueNumeric);
        const existing = buckets.get(key);
        if (existing) {
            existing.sum += v;
            existing.count += 1;
            existing.last = v;
        } else {
            buckets.set(key, { date: key, sum: v, count: 1, last: v });
        }
    }
    const sorted = Array.from(buckets.values()).sort((a, b) => a.date.localeCompare(b.date));

    let cumulative = 0;
    const points: ChartPoint[] = sorted.map((b) => {
        let value: number;
        if (config.aggregation === 'cumulative') {
            cumulative += b.sum;
            value = cumulative;
        } else if (config.aggregation === 'daily_avg' || config.aggregation === 'weekly_avg') {
            value = b.sum / b.count;
        } else {
            // 'raw' — take the last value for the day if multiple exist (rare
            // given UNIQUE (target, session)).
            value = b.last;
        }
        return {
            date: b.date,
            label: formatLabel(b.date, config.aggregation),
            value: Number.isFinite(value) ? Number(value.toFixed(2)) : 0,
            count: b.count,
        };
    });

    if (config.showTrendline) {
        const fit = fitTrend(points);
        if (fit) {
            for (const p of points) p.trend = Number(fit(p.date).toFixed(2));
        }
    }

    const criterionValue =
        config.showCriterion && target.masteryValue != null ? Number(target.masteryValue) : null;

    const yDomain: [number | 'auto', number | 'auto'] = [
        config.yAxisMin !== null ? config.yAxisMin : 'auto',
        config.yAxisMax !== null ? config.yAxisMax : 'auto',
    ];

    return {
        points,
        criterionValue,
        unitSuffix: unitSuffixFor(target.dataType),
        yDomain,
    };
}
